import { useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import { ArrowExternalLink } from 'src/components/Icons/ArrowExternalLink'
import { Blog404BackArrowIcon } from 'src/components/Icons/blog/Blog404BackArrowIcon'
import { StarsIcon } from 'src/components/product/CustomerReviews/Icons/StarsIcon'
import type { ReviewProductPreview } from 'src/utils/reviews/fetchReviewProductPreview'
import { REVIEW_INVALID_CODE_MESSAGE } from 'src/utils/reviews/getReviewUrl'

type ReviewRedirectPageProps = {
  reviewUrl?: string
  product?: ReviewProductPreview | null
}

export function ReviewRedirectPage({
  reviewUrl,
  product,
}: ReviewRedirectPageProps) {
  const hasOpenedRef = useRef(false)

  useEffect(() => {
    if (!reviewUrl || hasOpenedRef.current) {
      return
    }

    hasOpenedRef.current = true
    window.open(reviewUrl, '_blank', 'noopener,noreferrer')
  }, [reviewUrl])

  const productName = product?.name?.trim()
  const productImageUrl = product?.imageUrl?.trim()

  return (
    <div className="flex flex-1 flex-col items-center justify-center py-12 px-4 max-w-[1280px] mx-auto md:flex-row md:items-center md:gap-16 md:py-20 lg:gap-24">
      {reviewUrl && productImageUrl ? (
        <div className="flex w-full max-w-[560px] shrink-0 justify-center md:w-1/2 md:justify-start">
          <img
            className="h-auto w-full max-w-[360px] object-cover md:max-w-[560px]"
            src={productImageUrl}
            alt={product?.imageAlt ?? 'Foto do produto'}
          />
        </div>
      ) : null}

      <div className="mt-10 flex w-full flex-col items-center text-center md:mt-0 md:w-1/2 md:max-w-[480px] md:items-start md:text-left">
        {reviewUrl ? (
          <>
            <h1 className="mobile-heading-title2 desktop-heading-title2 text-restructure-primary">
              Avalie seu produto
            </h1>
            {productName ? (
              <p className="mt-2 desktop-body-regular-text1 text-restructure-secondary">
                {productName}
              </p>
            ) : null}
            <p className="mt-4 desktop-body-regular-text1 text-restructure-secondary">
              Sua opinião é importante para nós e para outros clientes.
            </p>
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 text-restructure-primary-inverted bg-restructure-background-primary-inverted text-center rounded-[360px] py-3 px-8 no-underline"
            >
              <StarsIcon width={16} height={16} fill="currentColor" />
              Avaliar produto
            </a>
            <p className="mt-4 inline-flex items-center gap-2 desktop-body-regular-text3 text-restructure-secondary">
              <ArrowExternalLink fill="currentColor" size="16" />
              <span>A avaliação abrirá em uma nova aba</span>
            </p>
          </>
        ) : (
          <>
            <h1 className="mobile-heading-title2 desktop-heading-title2 text-restructure-primary">
              Não foi possível abrir a avaliação
            </h1>
            <p
              role="alert"
              className="mt-4 desktop-body-regular-text1 text-restructure-secondary"
            >
              {REVIEW_INVALID_CODE_MESSAGE}
            </p>
            <Link
              to="/"
              aria-label="Voltar para a loja"
              className="mt-8 inline-flex items-center gap-2 text-restructure-primary no-underline"
            >
              <Blog404BackArrowIcon /> <span>Voltar para a loja</span>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
